import type { Race } from '@character-creator/types';
import { z } from 'zod';
import { RaceSchema } from '../schemas';

/**
 * SRD 5.1 race data
 * All data is validated against RaceSchema at module load time
 */
const rawRaces: Race[] = [
  {
    id: 'dwarf',
    name: 'Dwarf',
    description:
      'Bold and hardy, dwarves are known as skilled warriors, miners, and workers of stone and metal.',
    abilityBonuses: [{ ability: 'constitution', bonus: 2 }],
    age: 'Dwarves mature at the same rate as humans, but they are considered young until they reach the age of 50. On average, they live about 350 years.',
    size: 'medium',
    sizeDescription:
      'Dwarves stand between 4 and 5 feet tall and average about 150 pounds.',
    speed: 25,
    languages: ['Common', 'Dwarvish'],
    traits: [
      {
        id: 'darkvision',
        name: 'Darkvision',
        description:
          'You can see in dim light within 60 feet of you as if it were bright light, and in darkness as if it were dim light.',
      },
      {
        id: 'dwarven-resilience',
        name: 'Dwarven Resilience',
        description:
          'You have advantage on saving throws against poison, and you have resistance against poison damage.',
      },
      {
        id: 'dwarven-combat-training',
        name: 'Dwarven Combat Training',
        description:
          'You have proficiency with the battleaxe, handaxe, light hammer, and warhammer.',
      },
      {
        id: 'tool-proficiency',
        name: 'Tool Proficiency',
        description:
          "You gain proficiency with the artisan's tools of your choice: smith's tools, brewer's supplies, or mason's tools.",
      },
      {
        id: 'stonecunning',
        name: 'Stonecunning',
        description:
          'Whenever you make an Intelligence (History) check related to the origin of stonework, you are considered proficient in the History skill and add double your proficiency bonus to the check.',
      },
    ],
    subraces: [
      {
        id: 'hill-dwarf',
        name: 'Hill Dwarf',
        description:
          'As a hill dwarf, you have keen senses, deep intuition, and remarkable resilience.',
        abilityBonuses: [{ ability: 'wisdom', bonus: 1 }],
        traits: [
          {
            id: 'dwarven-toughness',
            name: 'Dwarven Toughness',
            description:
              'Your hit point maximum increases by 1, and it increases by 1 every time you gain a level.',
          },
        ],
      },
    ],
    source: 'srd-5.1',
  },
  {
    id: 'elf',
    name: 'Elf',
    description:
      'Elves are a magical people of otherworldly grace, living in the world but not entirely part of it.',
    abilityBonuses: [{ ability: 'dexterity', bonus: 2 }],
    age: 'Although elves reach physical maturity at about the same age as humans, the elven understanding of adulthood goes beyond physical growth to encompass worldly experience. An elf typically claims adulthood and an adult name around the age of 100 and can live to be 750 years old.',
    size: 'medium',
    sizeDescription:
      'Elves range from under 5 to over 6 feet tall and have slender builds.',
    speed: 30,
    languages: ['Common', 'Elvish'],
    traits: [
      {
        id: 'darkvision',
        name: 'Darkvision',
        description:
          'You can see in dim light within 60 feet of you as if it were bright light, and in darkness as if it were dim light.',
      },
      {
        id: 'keen-senses',
        name: 'Keen Senses',
        description: 'You have proficiency in the Perception skill.',
      },
      {
        id: 'fey-ancestry',
        name: 'Fey Ancestry',
        description:
          "You have advantage on saving throws against being charmed, and magic can't put you to sleep.",
      },
      {
        id: 'trance',
        name: 'Trance',
        description:
          "Elves don't need to sleep. Instead, they meditate deeply, remaining semiconscious, for 4 hours a day.",
      },
    ],
    subraces: [
      {
        id: 'high-elf',
        name: 'High Elf',
        description:
          'As a high elf, you have a keen mind and a mastery of at least the basics of magic.',
        abilityBonuses: [{ ability: 'intelligence', bonus: 1 }],
        traits: [
          {
            id: 'elf-weapon-training',
            name: 'Elf Weapon Training',
            description:
              'You have proficiency with the longsword, shortsword, shortbow, and longbow.',
          },
          {
            id: 'cantrip',
            name: 'Cantrip',
            description:
              'You know one cantrip of your choice from the wizard spell list. Intelligence is your spellcasting ability for it.',
          },
          {
            id: 'extra-language',
            name: 'Extra Language',
            description: 'You can speak, read, and write one extra language of your choice.',
          },
        ],
      },
    ],
    source: 'srd-5.1',
  },
  {
    id: 'halfling',
    name: 'Halfling',
    description:
      'The diminutive halflings survive in a world full of larger creatures by avoiding notice or, barring that, avoiding offense.',
    abilityBonuses: [{ ability: 'dexterity', bonus: 2 }],
    age: 'A halfling reaches adulthood at the age of 20 and generally lives into the middle of his or her second century.',
    size: 'small',
    sizeDescription: 'Halflings average about 3 feet tall and weigh about 40 pounds.',
    speed: 25,
    languages: ['Common', 'Halfling'],
    traits: [
      {
        id: 'lucky',
        name: 'Lucky',
        description:
          'When you roll a 1 on the d20 for an attack roll, ability check, or saving throw, you can reroll the die and must use the new roll.',
      },
      {
        id: 'brave',
        name: 'Brave',
        description: 'You have advantage on saving throws against being frightened.',
      },
      {
        id: 'halfling-nimbleness',
        name: 'Halfling Nimbleness',
        description:
          'You can move through the space of any creature that is of a size larger than yours.',
      },
    ],
    subraces: [
      {
        id: 'lightfoot',
        name: 'Lightfoot',
        description:
          'As a lightfoot halfling, you can easily hide from notice, even using other people as cover.',
        abilityBonuses: [{ ability: 'charisma', bonus: 1 }],
        traits: [
          {
            id: 'naturally-stealthy',
            name: 'Naturally Stealthy',
            description:
              'You can attempt to hide even when you are obscured only by a creature that is at least one size larger than you.',
          },
        ],
      },
    ],
    source: 'srd-5.1',
  },
  {
    id: 'human',
    name: 'Human',
    description:
      'Humans are the most adaptable and ambitious people among the common races. Whatever drives them, humans are the innovators, the achievers, and the pioneers of the worlds.',
    abilityBonuses: [
      { ability: 'strength', bonus: 1 },
      { ability: 'dexterity', bonus: 1 },
      { ability: 'constitution', bonus: 1 },
      { ability: 'intelligence', bonus: 1 },
      { ability: 'wisdom', bonus: 1 },
      { ability: 'charisma', bonus: 1 },
    ],
    age: 'Humans reach adulthood in their late teens and live less than a century.',
    size: 'medium',
    sizeDescription:
      'Humans vary widely in height and build, from barely 5 feet to well over 6 feet tall.',
    speed: 30,
    languages: ['Common'],
    languageChoice: 1,
    traits: [],
    source: 'srd-5.1',
  },
  {
    id: 'dragonborn',
    name: 'Dragonborn',
    description:
      'Born of dragons, as their name proclaims, the dragonborn walk proudly through a world that greets them with fearful incomprehension.',
    abilityBonuses: [
      { ability: 'strength', bonus: 2 },
      { ability: 'charisma', bonus: 1 },
    ],
    age: 'Young dragonborn grow quickly. They walk hours after hatching, attain the size and development of a 10-year-old human child by the age of 3, and reach adulthood by 15. They live to be around 80.',
    size: 'medium',
    sizeDescription:
      'Dragonborn are taller and heavier than humans, standing well over 6 feet tall and averaging almost 250 pounds.',
    speed: 30,
    languages: ['Common', 'Draconic'],
    traits: [
      {
        id: 'draconic-ancestry',
        name: 'Draconic Ancestry',
        description:
          'You have draconic ancestry. Choose one type of dragon from the Draconic Ancestry table. Your breath weapon and damage resistance are determined by the dragon type.',
      },
      {
        id: 'breath-weapon',
        name: 'Breath Weapon',
        description:
          'You can use your action to exhale destructive energy. Your draconic ancestry determines the size, shape, and damage type of the exhalation.',
      },
      {
        id: 'damage-resistance',
        name: 'Damage Resistance',
        description:
          'You have resistance to the damage type associated with your draconic ancestry.',
      },
    ],
    source: 'srd-5.1',
  },
  {
    id: 'gnome',
    name: 'Gnome',
    description:
      "A gnome's energy and enthusiasm for living shines through every inch of his or her tiny body.",
    abilityBonuses: [{ ability: 'intelligence', bonus: 2 }],
    age: 'Gnomes mature at the same rate humans do, and most are expected to settle down into an adult life by around age 40. They can live 350 to almost 500 years.',
    size: 'small',
    sizeDescription: 'Gnomes are between 3 and 4 feet tall and average about 40 pounds.',
    speed: 25,
    languages: ['Common', 'Gnomish'],
    traits: [
      {
        id: 'darkvision',
        name: 'Darkvision',
        description:
          'You can see in dim light within 60 feet of you as if it were bright light, and in darkness as if it were dim light.',
      },
      {
        id: 'gnome-cunning',
        name: 'Gnome Cunning',
        description:
          'You have advantage on all Intelligence, Wisdom, and Charisma saving throws against magic.',
      },
    ],
    subraces: [
      {
        id: 'rock-gnome',
        name: 'Rock Gnome',
        description:
          'As a rock gnome, you have a natural inventiveness and hardiness beyond that of other gnomes.',
        abilityBonuses: [{ ability: 'constitution', bonus: 1 }],
        traits: [
          {
            id: 'artificers-lore',
            name: "Artificer's Lore",
            description:
              'Whenever you make an Intelligence (History) check related to magic items, alchemical objects, or technological devices, you can add twice your proficiency bonus.',
          },
          {
            id: 'tinker',
            name: 'Tinker',
            description:
              "You have proficiency with artisan's tools (tinker's tools). Using those tools, you can spend 1 hour and 10 gp worth of materials to construct a Tiny clockwork device.",
          },
        ],
      },
    ],
    source: 'srd-5.1',
  },
  {
    id: 'half-elf',
    name: 'Half-Elf',
    description:
      'Half-elves combine what some say are the best qualities of their elf and human parents.',
    abilityBonuses: [{ ability: 'charisma', bonus: 2 }],
    abilityBonusChoice: {
      count: 2,
      amount: 1,
      from: ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom'],
    },
    age: 'Half-elves mature at the same rate humans do and reach adulthood around the age of 20. They live much longer than humans, however, often exceeding 180 years.',
    size: 'medium',
    sizeDescription:
      'Half-elves are about the same size as humans, ranging from 5 to 6 feet tall.',
    speed: 30,
    languages: ['Common', 'Elvish'],
    languageChoice: 1,
    traits: [
      {
        id: 'darkvision',
        name: 'Darkvision',
        description:
          'You can see in dim light within 60 feet of you as if it were bright light, and in darkness as if it were dim light.',
      },
      {
        id: 'fey-ancestry',
        name: 'Fey Ancestry',
        description:
          "You have advantage on saving throws against being charmed, and magic can't put you to sleep.",
      },
      {
        id: 'skill-versatility',
        name: 'Skill Versatility',
        description: 'You gain proficiency in two skills of your choice.',
      },
    ],
    source: 'srd-5.1',
  },
  {
    id: 'half-orc',
    name: 'Half-Orc',
    description:
      "Half-orcs' grayish pigmentation, sloping foreheads, jutting jaws, prominent teeth, and towering builds make their orcish heritage plain for all to see.",
    abilityBonuses: [
      { ability: 'strength', bonus: 2 },
      { ability: 'constitution', bonus: 1 },
    ],
    age: 'Half-orcs mature a little faster than humans, reaching adulthood around age 14. They age noticeably faster and rarely live longer than 75 years.',
    size: 'medium',
    sizeDescription:
      'Half-orcs are somewhat larger and bulkier than humans, and they range from 5 to well over 6 feet tall.',
    speed: 30,
    languages: ['Common', 'Orc'],
    traits: [
      {
        id: 'darkvision',
        name: 'Darkvision',
        description:
          'You can see in dim light within 60 feet of you as if it were bright light, and in darkness as if it were dim light.',
      },
      {
        id: 'menacing',
        name: 'Menacing',
        description: 'You gain proficiency in the Intimidation skill.',
      },
      {
        id: 'relentless-endurance',
        name: 'Relentless Endurance',
        description:
          "When you are reduced to 0 hit points but not killed outright, you can drop to 1 hit point instead. You can't use this feature again until you finish a long rest.",
      },
      {
        id: 'savage-attacks',
        name: 'Savage Attacks',
        description:
          "When you score a critical hit with a melee weapon attack, you can roll one of the weapon's damage dice one additional time and add it to the extra damage of the critical hit.",
      },
    ],
    source: 'srd-5.1',
  },
  {
    id: 'tiefling',
    name: 'Tiefling',
    description:
      'To be greeted with stares and whispers, to suffer violence and insult on the street, to see mistrust and fear in every eye: this is the lot of the tiefling.',
    abilityBonuses: [
      { ability: 'intelligence', bonus: 1 },
      { ability: 'charisma', bonus: 2 },
    ],
    age: 'Tieflings mature at the same rate as humans but live a few years longer.',
    size: 'medium',
    sizeDescription: 'Tieflings are about the same size and build as humans.',
    speed: 30,
    languages: ['Common', 'Infernal'],
    traits: [
      {
        id: 'darkvision',
        name: 'Darkvision',
        description:
          'You can see in dim light within 60 feet of you as if it were bright light, and in darkness as if it were dim light.',
      },
      {
        id: 'hellish-resistance',
        name: 'Hellish Resistance',
        description: 'You have resistance to fire damage.',
      },
      {
        id: 'infernal-legacy',
        name: 'Infernal Legacy',
        description:
          'You know the thaumaturgy cantrip. When you reach 3rd level, you can cast the hellish rebuke spell as a 2nd-level spell once. When you reach 5th level, you can cast the darkness spell once. Charisma is your spellcasting ability for these spells.',
        spellGrants: {
          cantrips: ['thaumaturgy'],
          spells: [
            { spellId: 'hellish-rebuke', level: 3, usesPerRest: 1, restType: 'long' },
            { spellId: 'darkness', level: 5, usesPerRest: 1, restType: 'long' },
          ],
          spellcastingAbility: 'charisma',
        },
      },
    ],
    source: 'srd-5.1',
  },
];

/**
 * Validate all race data at module load time
 * This ensures data integrity and catches errors early in development
 */
function validateRaces(data: Race[]): Race[] {
  const RaceArraySchema = z.array(RaceSchema);
  const result = RaceArraySchema.safeParse(data);

  if (!result.success) {
    const formatted = JSON.stringify(result.error.format(), null, 2);
    throw new Error(`Race data validation failed:\n${formatted}`);
  }

  return data;
}

export const races: Race[] = validateRaces(rawRaces);
